import { ArrowLeft } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Tag } from '@/components/ui/Tag'
import type { SelectOption } from '@/components/ui/Select'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { describeProblem } from '@/lib/problems'
import { normaliseDecimal } from '@/features/finance/lines'
import { useCaseTranches } from '@/features/finance/api/useFinance'
import { useAmendInvoice, useInvoice } from '@/features/finance/api/useInvoices'
import {
  DocumentActions,
  DocumentStatusPill,
} from '@/features/finance/components/DocumentActions'
import { DocumentPreview } from '@/features/finance/components/DocumentPreview'
import { InvoiceLineEditor } from '@/features/finance/components/InvoiceLineEditor'
import { VatBlock } from '@/features/finance/components/InvoiceSheets'
import {
  INVOICE_CURRENCIES,
  currencyLabel,
  isAmountLike,
  rateCeiling,
  sameAmount,
  type Invoice,
  type InvoiceCurrency,
  type InvoiceHeaderPayload,
} from '@/features/finance/invoice'
import { toCaseDocumentFromInvoice, type CaseTrancheRow } from '@/types/domain'

/* The invoice's workplace — one page per invoice, at
 * `/cases/:caseId/invoices/:invoiceId`.
 *
 * ── Why this is a page and not the third sheet in a chain ──────────────────
 *
 * Raising a client invoice used to be three stacked modals: a raise form, an
 * editor sheet over it, a confirmation over that. Each of them was 440px wide,
 * and the invoice is a document that PRINTS ON A4 and is read across. Three
 * things have to be in view at once for the work to be possible at all —
 * the charges, the approved rate they are judged against (AD-10), and the
 * booking that turns them into USD (AD-9) — and a column can hold one of them.
 * So the record gets a URL, which also means a colleague can be sent the
 * invoice rather than a case and a set of instructions.
 *
 * ── What this page does NOT own ────────────────────────────────────────────
 *
 * Almost everything on it is imported, deliberately, because every one of these
 * rules already exists somewhere and a second copy is a second answer:
 *
 *   · `InvoiceLineEditor` — one round trip per charge, so the AD-10 refusal
 *     lands beside the amount that caused it while the typist is still looking
 *     at it. Not re-implemented, not wrapped, not batched.
 *   · `DocumentActions` — Generate, then Issue, and Issue refused with a
 *     SENTENCE while `documentId` is absent. `documentId` is the whole state
 *     machine (see `useFinance.ts`): the server clears it on every draft write,
 *     so its presence means "a current render exists" and nothing else on this
 *     side is allowed to answer that question.
 *   · `DocumentPreview` — the archived PDF, for an invoice that has been
 *     issued and can no longer be edited.
 *   · `VatBlock` — shared with the editor sheet, because the header PUT is a
 *     whole-record replace and two copies of this block is two chances for one
 *     of them to stop sending `vatRate`.
 *
 * ── The header form asks more than the design does, and has to ─────────────
 *
 * The mock's "what are we billing" block is four fields: the instalment, the
 * currency, the invoice date, the economic date. The PUT behind it replaces the
 * WHOLE header, so a field the form does not send is a field the save CLEARS —
 * `invoiceBody.change()` reads an absent `trancheId` as unlink, and the same is
 * true of the customer reference and the tax block. A form built to the mock
 * exactly would silently drop the client's own reference the first time
 * somebody corrected a date. So the reference and the tax block are on the card
 * too, where they can be seen and changed, rather than being round-tripped
 * invisibly through hidden state.
 */

export function CaseInvoicePage() {
  const { caseId = '', invoiceId = '' } = useParams()
  const { data, isPending, isError, error, refetch } = useInvoice(caseId, invoiceId)
  /* Read HERE and handed down rather than read where it is used. Three things
     need it — the title names the instalment, the header form offers the list,
     the mismatch banner compares against the chosen row — and the invoice
     itself carries the tranche's ID and nothing else about it, so the sequence
     a reader recognises can only come from this list. */
  const tranches = useCaseTranches(caseId)
  const rows = tranches.data ?? []
  const linked = rows.find((tranche) => tranche.id === data?.trancheId)

  /* The way back is rendered on ALL THREE branches, including the failures.
     A page reached by URL that answers 403 or 404 with nothing but an error box
     is a dead end: the reader has no way back to the case except the browser's
     own button, and on a phone opened from a chat message there is none. */
  return (
    <div className="flex flex-col gap-4 pb-24 md:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <BackToCase caseId={caseId} caseNumber={data?.caseNumber} />
          {data === undefined ? (
            <Skeleton className="h-6 w-56" />
          ) : (
            <InvoiceTitle caseId={caseId} invoice={data} tranche={linked} />
          )}
        </div>

        {/* ONE instance, placed once. On a phone it detaches into the bar
            pinned to the bottom of the viewport (UX-DR-27) and on a desktop it
            sits beside the title; rendering it twice would give a screen reader
            two "Issue" buttons and a test two matches for one control.

            An ISSUED invoice offers neither verb, so the bar is absent rather
            than empty: re-rendering would replace the archived bytes the client
            is holding a copy of, and issuing again is refused by the server
            anyway. The page shows the document instead of controls that lie
            about it. */}
        {data === undefined || data.status === 'issued' ? null : (
          <div
            className={cn(
              'shrink-0',
              'max-md:bg-surface max-md:border-border-subtle max-md:fixed',
              'max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:border-t',
              'max-md:px-4 max-md:py-3',
            )}
          >
            <DocumentActions record={toCaseDocumentFromInvoice(data)} />
          </div>
        )}
      </div>

      {isPending ? (
        <Skeleton className="h-96 rounded-[var(--radius-DEFAULT)]" />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <InvoiceDesk caseId={caseId} invoice={data} tranches={rows} tranche={linked} />
      )}
    </div>
  )
}

/** The link back to where the invoice was opened from.
 *
 *  `?section=documents` rather than the bare case: both surfaces that open an
 *  invoice are that section, and landing on the case's Overview would make the
 *  reader find their way back to the paper they were just looking at. */
function BackToCase({ caseId, caseNumber }: { caseId: string; caseNumber?: string }) {
  const { t } = useTranslation()
  return (
    <Link
      to={`/cases/${caseId}?section=documents`}
      className="text-text-link hover:text-text-link-hover flex w-fit items-center gap-1.5 text-[12px]"
    >
      <ArrowLeft size={13} aria-hidden />
      {caseNumber === undefined
        ? t('finance.invoiceDesk.back')
        : `${caseNumber} · ${t('finance.invoiceDesk.back')}`}
    </Link>
  )
}

/** The identity line: what this invoice is, where it stands, and who owes it. */
function InvoiceTitle({
  caseId,
  invoice,
  tranche,
}: {
  caseId: string
  invoice: Invoice
  tranche?: CaseTrancheRow
}) {
  const { t } = useTranslation()
  /* The tranche is a LINK, to the case's money section where the schedule
     lives: an invoice that closes an instalment and a schedule that expects it
     are two views of one obligation, and a reader who arrived here from a chat
     message has no other way across.

     Rendered only once the instalment can be NAMED. The invoice carries an id
     and no sequence, so before the tranche list lands there is no number to
     print — and an invented "tranche 1" on a demand for money is worse than no
     link at all. */
  const trancheLink =
    invoice.trancheId === null || tranche === undefined ? null : (
      <Link
        to={`/cases/${caseId}?section=money`}
        className="text-text-link hover:text-text-link-hover"
      >
        {t('finance.invoiceDesk.trancheN', { n: tranche.sequence })}
      </Link>
    )

  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5">
        {invoice.invoiceNumber === null ? (
          <h1 className="text-text-heading text-[19px] leading-tight font-normal tracking-[-0.4px]">
            {t('finance.invoiceDesk.title')}
          </h1>
        ) : (
          /* A minted number is a record IDENTIFIER, which is one of the four
             places weight 600 is allowed, and it is set in the mono face the
             rest of this application sets numbers in. */
          <h1 className="text-text-heading font-mono text-[17px] leading-tight font-semibold">
            {invoice.invoiceNumber}
          </h1>
        )}
        {trancheLink === null ? null : (
          <span className="text-text-muted text-[13px]">· {trancheLink}</span>
        )}
        <DocumentStatusPill record={toCaseDocumentFromInvoice(invoice)} />
      </div>

      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {invoice.clientName} ·{' '}
        {invoice.issuedAt === null
          ? t('finance.invoiceDesk.numberOnIssue')
          : t('finance.invoiceDesk.issuedOn', { date: formatDate(invoice.issuedAt) })}
      </p>
    </>
  )
}

function InvoiceDesk({
  caseId,
  invoice,
  tranches,
  tranche: linked,
}: {
  caseId: string
  invoice: Invoice
  tranches: CaseTrancheRow[]
  /** The row `invoice.trancheId` names, once the list above has arrived. */
  tranche?: CaseTrancheRow
}) {
  const { t } = useTranslation()
  const issued = invoice.status === 'issued'

  return (
    <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {issued ? (
          <IssuedFacts invoice={invoice} tranche={linked} />
        ) : (
          <WhatWeBill caseId={caseId} invoice={invoice} tranches={tranches} />
        )}

        <Card>
          <CardHeader
            action={
              invoice.quoteNumber === null ? null : (
                <Tag tone="brand">{t('finance.invoiceDesk.fromQuote')}</Tag>
              )
            }
          >
            <CardTitle>{t('finance.invoiceDesk.lines')}</CardTitle>
            <CardSubtitle>
              {invoice.quoteNumber === null
                ? t('finance.invoiceDesk.linesOwn')
                : t('finance.invoiceDesk.linesFromQuote', {
                    quote: invoice.quoteNumber,
                  })}
            </CardSubtitle>
          </CardHeader>

          <CardBody className="flex flex-col gap-3">
            <TrancheMismatch invoice={invoice} tranche={linked} />

            {issued ? (
              /* No editor at all on an issued document — not a disabled one.
                 The client is holding paper with this number on it, and a
                 correction is a NEW invoice (AD-24). What is offered instead is
                 the archived PDF itself, which is the thing they were sent. */
              <>
                <p className="text-text-muted text-[12.5px] leading-snug font-light">
                  {t('finance.documents.issuedReadOnly')}
                </p>
                <DocumentPreview
                  record={toCaseDocumentFromInvoice(invoice)}
                  lines={invoice.lines}
                />
              </>
            ) : (
              <InvoiceLineEditor caseId={caseId} invoice={invoice} />
            )}
          </CardBody>
        </Card>
      </div>

      <div className="flex w-full flex-col gap-4 xl:w-[380px] xl:shrink-0">
        <CeilingCard invoice={invoice} />
        <BookingCard invoice={invoice} />
        <PrintsCard invoice={invoice} />
        {issued ? null : <NextStepsCard />}
      </div>
    </div>
  )
}

/** The header form: what is being billed, and against which instalment.
 *
 *  Every field on it is part of ONE payload, and the payload replaces the whole
 *  header — see this file's opening note on why the reference and the tax block
 *  are here even though the design shows four fields. */
function WhatWeBill({
  caseId,
  invoice,
  tranches,
}: {
  caseId: string
  invoice: Invoice
  tranches: CaseTrancheRow[]
}) {
  const { t } = useTranslation()
  const amend = useAmendInvoice(caseId, invoice.id)

  const [draft, setDraft, touched, markTouched] = useFormDraft<InvoiceHeaderPayload>(
    invoice.id,
    () => ({
      invoiceDate: invoice.invoiceDate,
      customerReference: invoice.customerReference ?? '',
      // `''` is the stored answer for an invoice outside the series, and it is
      // what the select's first option carries — see `NewInvoicePayload`.
      trancheId: invoice.trancheId ?? '',
      vatApplies: invoice.vatApplies,
      vatRate: invoice.vatRate ?? '',
    }),
  )

  const options = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = [
      { value: '', label: t('finance.invoiceDesk.offSchedule') },
      ...tranches.map((tranche) => ({
        value: tranche.id,
        label: t('finance.invoiceDesk.trancheOption', {
          n: tranche.sequence,
          amount: formatMoney(tranche.amount, tranche.currency),
        }),
        hint:
          tranche.expectedDate === null ? undefined : formatDate(tranche.expectedDate),
      })),
    ]
    /* The invoice's own link, when the tranche list has not arrived or could
       not be read. Without this the select falls back to its first option and
       states "off schedule" over a record that says otherwise — a control that
       misreports what is stored is worse than one that is still loading. */
    const current = draft.trancheId ?? ''
    if (current !== '' && !list.some((option) => option.value === current)) {
      list.push({ value: current, label: t('finance.invoiceDesk.trancheLinked') })
    }
    return list
  }, [draft.trancheId, t, tranches])

  const invoiceDateError =
    touched && draft.invoiceDate.trim() === '' ? t('common.form.required') : undefined
  const vatRateError =
    touched && draft.vatApplies && !isAmountLike(draft.vatRate)
      ? t('common.form.invalidNumber')
      : undefined
  const invalid =
    draft.invoiceDate.trim() === '' || (draft.vatApplies && !isAmountLike(draft.vatRate))

  const chosen = tranches.find((tranche) => tranche.id === draft.trancheId)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.whatWeBill')}</CardTitle>
      </CardHeader>

      <form
        className="flex flex-col gap-3 px-[18px] py-4"
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          markTouched()
          if (invalid || amend.isPending) return
          // Normalised on the way out, as every figure this feature sends is: a
          // Russian keyboard produces "20,5", and the check above accepts it
          // because it normalises before testing.
          amend.mutate({ ...draft, vatRate: normaliseDecimal(draft.vatRate) })
        }}
        noValidate
      >
        {amend.isError ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {t(describeProblem(amend.error).detailKey)}
          </p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Field label={t('finance.invoiceDesk.tranche')}>
            {(props) => (
              <Select
                value={draft.trancheId ?? ''}
                onChange={(trancheId) => setDraft({ ...draft, trancheId })}
                options={options}
                {...props}
              />
            )}
          </Field>

          {/* Shown, never offered. The currency is one of the five facts AD-9
              freezes together at creation, and a select that greyed itself out
              would be describing that rule as a UI state. */}
          <Field
            label={t('finance.invoices.currency')}
            hint={t('finance.invoiceDesk.currencyFrozen')}
          >
            {(props) => (
              <Input value={printedCurrency(invoice.currency)} readOnly {...props} />
            )}
          </Field>

          <Field
            label={t('finance.invoices.invoiceDate')}
            required
            error={invoiceDateError}
          >
            {(props) => (
              <DatePicker
                value={draft.invoiceDate}
                onChange={(invoiceDate) => setDraft({ ...draft, invoiceDate })}
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('finance.invoices.economicDate')}
            hint={t('finance.invoices.economicDateFrozen')}
          >
            {(props) => (
              <Input value={invoice.economicDate} readOnly className="asa-num" {...props} />
            )}
          </Field>
        </div>

        <Field
          label={t('finance.invoices.customerReference')}
          hint={t('finance.invoices.customerReferenceHint')}
          className="sm:max-w-xs"
        >
          {(props) => (
            <Input
              value={draft.customerReference}
              onChange={(e) => setDraft({ ...draft, customerReference: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <VatBlock
          applies={draft.vatApplies}
          rate={draft.vatRate}
          rateError={vatRateError}
          amount={invoice.vatApplies ? invoice.vatAmount : null}
          currency={invoice.currency}
          onChange={(change) => setDraft({ ...draft, ...change })}
        />

        <div>
          <Button type="submit" size="sm" disabled={amend.isPending}>
            {amend.isPending ? t('common.form.saving') : t('finance.invoices.saveHeader')}
          </Button>
        </div>
      </form>

      {/* What ISSUING will do to the payment schedule, said before the press
          rather than discovered after it. */}
      <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
        {chosen === undefined
          ? t('finance.invoiceDesk.offScheduleNote')
          : t('finance.invoiceDesk.trancheClosesNote', { n: chosen.sequence })}
      </div>
    </Card>
  )
}

/** The issued invoice's facts, in place of the header form.
 *
 *  Story-for-story the States sheet's fourth case: a number, what it closed,
 *  and where its settlement stands. No control anywhere on it. */
function IssuedFacts({
  invoice,
  tranche,
}: {
  invoice: Invoice
  tranche?: CaseTrancheRow
}) {
  const { t } = useTranslation()
  const record = toCaseDocumentFromInvoice(invoice)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.whatWeBill')}</CardTitle>
        <CardSubtitle>
          {tranche === undefined
            ? t('finance.invoiceDesk.offSchedule')
            : t('finance.invoiceDesk.closedTranche', { n: tranche.sequence })}
        </CardSubtitle>
      </CardHeader>

      <CardBody className="grid gap-4 sm:grid-cols-3">
        <Figure label={t('finance.invoiceDesk.faceValue')}>
          {formatMoney(record.total.amount, record.total.currency)}
        </Figure>
        {/* Positive tone on money that has ARRIVED, which is the one place this
            palette calls a figure good news. */}
        <Figure label={t('finance.invoiceDesk.received')} tone="positive">
          {formatMoney(record.paid.amount, record.paid.currency)}
        </Figure>
        <Figure label={t('finance.invoiceDesk.outstanding')}>
          {formatMoney(record.outstanding.amount, record.outstanding.currency)}
        </Figure>
      </CardBody>
    </Card>
  )
}

/** The gap between what a tranche expects and what the charges come to.
 *
 *  INFORMATIONAL, and deliberately so. Closing the gap means splitting the
 *  charges, moving the invoice off the schedule or re-cutting the instalment,
 *  and each of those is a judgement with a different consequence for the rest
 *  of the schedule — a button that picked one for the reader would be this
 *  screen deciding how a contract is billed.
 *
 *  Only when the two figures are in the SAME currency. A tranche booked in EUR
 *  beside an invoice printed in USD can be compared at their booked rates, but
 *  the sentence would then name two figures the reader cannot add up, and the
 *  banner exists to be acted on rather than puzzled over. */
function TrancheMismatch({
  invoice,
  tranche,
}: {
  invoice: Invoice
  tranche?: CaseTrancheRow
}) {
  const { t } = useTranslation()
  if (tranche === undefined) return null
  if (tranche.currency !== invoice.currency) return null
  if (sameAmount(tranche.amount, invoice.totalAmount)) return null

  return (
    <Banner
      tone="attention"
      title={t('finance.invoiceDesk.mismatchTitle', { n: tranche.sequence })}
    >
      {t('finance.invoiceDesk.mismatchBody', {
        n: tranche.sequence,
        lines: formatMoney(invoice.totalAmount, invoice.currency),
        tranche: formatMoney(tranche.amount, tranche.currency),
      })}
    </Banner>
  )
}

/** The AD-10 ceiling, standing rather than as a refusal.
 *
 *  Four figures and a bar: what was agreed, what earlier invoices took, what
 *  this one takes, and what is left. The arithmetic is `rateCeiling`'s, which
 *  runs on exact cents the same way the refusal message does — the two are read
 *  side by side and a display figure that disagreed with a refusal by a cent
 *  would send somebody to correct a charge that was right. */
function CeilingCard({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation()
  const ceiling = rateCeiling(invoice)

  if (ceiling === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('finance.invoiceDesk.noCeilingTitle')}</CardTitle>
          <CardSubtitle>{t('finance.invoiceDesk.noCeilingBody')}</CardSubtitle>
        </CardHeader>
        <CardBody className="pt-2" />
      </Card>
    )
  }

  /* Percentages for the bar, and the ONLY place on this page a money figure is
     turned into a proportion. It is decoration over the numbers printed beside
     it — colour never carries meaning alone here — so a rounding of a tenth of
     a percent costs nothing, and a ceiling of zero would otherwise divide by it. */
  const width = (part: number) =>
    ceiling.approvedUsd <= 0
      ? '0%'
      : `${Math.max(0, Math.min(100, (part / ceiling.approvedUsd) * 100))}%`

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.ceiling')}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2.5">
        <Row label={t('finance.invoiceDesk.ceilingApproved')} strong>
          {formatMoney(ceiling.approvedUsd, 'USD')}
        </Row>
        <Row label={t('finance.invoiceDesk.ceilingEarlier')}>
          {formatMoney(ceiling.earlierUsd, 'USD')}
        </Row>
        <Row label={t('finance.invoiceDesk.ceilingThis')} tone="brand">
          {formatMoney(ceiling.thisInvoiceUsd, 'USD')}
        </Row>

        <div
          aria-hidden
          className="bg-neutral-100 flex h-1.5 overflow-hidden rounded-full"
        >
          <span
            className="bg-positive"
            style={{ width: width(ceiling.earlierUsd) }}
          />
          <span className="bg-brand" style={{ width: width(ceiling.thisInvoiceUsd) }} />
        </div>

        <div className="border-line-hair border-t pt-2.5">
          {/* Negative headroom is STATED, not clamped: a case already past its
              ceiling is exactly the case somebody needs to see. */}
          <Row
            label={t('finance.invoiceDesk.ceilingRemaining')}
            strong
            tone={ceiling.remainingUsd < 0 ? 'negative' : undefined}
          >
            {formatMoney(ceiling.remainingUsd, 'USD')}
          </Row>
        </div>
      </CardBody>
    </Card>
  )
}

/** The AD-9 booking, so the USD figures above can be explained. */
function BookingCard({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.booking')}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2.5">
        <Row label={t('finance.invoiceDesk.bookingCurrency')}>
          {printedCurrency(invoice.currency)}
        </Row>
        <Row
          label={t('finance.invoiceDesk.bookingRate', {
            date: formatDate(invoice.fxRateDate),
          })}
        >
          {/* The rate keeps its stored precision. Rounded to two places it no
              longer reproduces the USD total printed above it. */}
          {formatNumber(invoice.fxRate, 6)}
        </Row>
        {invoice.vatApplies && invoice.vatAmount !== null ? (
          <Row label={t('finance.invoices.vat')}>
            {formatMoney(invoice.vatAmount, invoice.currency)}
          </Row>
        ) : null}
        <p className="text-text-muted text-[11.5px] leading-snug font-light">
          {t('finance.invoiceDesk.bookingNote')}
        </p>
      </CardBody>
    </Card>
  )
}

/** What the issuing entity puts on the paper (AC-5, FR-64).
 *
 *  Read-only with no control behind it, because there is no write path that
 *  would accept one — the two lines resolve from the entity and are changed in
 *  Settings, which the note says out loud so nobody hunts for the field. */
function PrintsCard({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.prints')}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-text-muted text-[12px]">
            {t('finance.invoices.paymentTerms')}
          </span>
          <span className="text-text-body text-[12.5px] leading-snug">
            {invoice.paymentTerms || t('common.empty')}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-text-muted text-[12px]">
            {t('finance.invoices.bankDetails')}
          </span>
          <span className="text-text-body text-[12.5px] leading-snug">
            {invoice.bankDetails || t('common.empty')}
          </span>
        </div>
        <p className="text-text-muted text-[11.5px] leading-snug font-light">
          {t('finance.invoices.termsNote')}
        </p>
      </CardBody>
    </Card>
  )
}

/** Draft → rendered → issued, in the reader's own words.
 *
 *  Three server calls and three decisions (UX-DR-15), and the middle one is the
 *  step people are surprised by: any edit to the draft clears `documentId` and
 *  the PDF has to be built again. Saying it here is cheaper than discovering it
 *  from a button that has quietly turned itself off. */
function NextStepsCard() {
  const { t } = useTranslation()
  const steps = [
    t('finance.invoiceDesk.nextOne'),
    t('finance.invoiceDesk.nextTwo'),
    t('finance.invoiceDesk.nextThree'),
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.invoiceDesk.next')}</CardTitle>
      </CardHeader>
      <CardBody>
        <ol className="flex flex-col gap-2.5">
          {steps.map((step, index) => (
            <li key={step} className="flex items-start gap-2.5">
              <span
                aria-hidden
                className={cn(
                  'mt-px flex size-[18px] shrink-0 items-center justify-center rounded-full',
                  'text-[10px] font-semibold',
                  index === 0
                    ? 'bg-brand-soft text-brand'
                    : 'bg-neutral-100 text-text-muted',
                )}
              >
                {index + 1}
              </span>
              <span
                className={cn(
                  'text-[12.5px] leading-snug',
                  index === 0 ? 'text-text-heading' : 'text-text-muted font-light',
                )}
              >
                {step}
              </span>
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  )
}

/** A label and a figure on one baseline — the shape every side-column row here
 *  takes, so the four cards read as one table rather than four. */
function Row({
  label,
  children,
  strong,
  tone,
}: {
  label: string
  children: ReactNode
  strong?: boolean
  tone?: 'brand' | 'negative'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted text-[12.5px]">{label}</span>
      <span
        className={cn(
          'asa-num shrink-0 text-[13px]',
          // Weight 600 is allowed on a computed total, which is what the two
          // `strong` rows are; the rest stay Regular.
          strong && 'text-[15px] font-semibold',
          tone === 'brand'
            ? 'text-brand'
            : tone === 'negative'
              ? 'text-negative'
              : 'text-text-heading',
        )}
      >
        {children}
      </span>
    </div>
  )
}

/** One stacked figure with its caption, for the issued invoice's settlement. */
function Figure({
  label,
  children,
  tone,
}: {
  label: string
  children: ReactNode
  tone?: 'positive'
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="asa-eyebrow">{label}</span>
      <span
        className={cn(
          'asa-num text-[17px] font-semibold',
          tone === 'positive' ? 'text-positive' : 'text-text-heading',
        )}
      >
        {children}
      </span>
    </div>
  )
}

/** The currency as the DESK spells it.
 *
 *  CNY travels on the wire and prints as RMB on screen — the one place two
 *  spellings of one currency are allowed to differ (see `currencyLabel`). Any
 *  code outside the three the trade invoices in is printed as it arrived rather
 *  than narrowed, because a currency this client does not recognise is still
 *  the currency on the paper. */
function printedCurrency(currency: string): string {
  return (INVOICE_CURRENCIES as readonly string[]).includes(currency)
    ? currencyLabel(currency as InvoiceCurrency)
    : currency
}
